import { useState, useEffect, useCallback } from 'react';
import RapportEditor from './RapportEditor';
import SystemReportEditor from './SystemReportEditor';
import { openReport, buildReportName } from '../lib/reportViewerBus';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SYSTEM_KEYS = { 'Demande': 'daj', 'Attestation': 'attestation' };

export default function GestionRapportsModal({ onClose, currentNoseqdaj, userRegion }) {
  const [rapports, setRapports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorId, setEditorId] = useState(null);
  const [sysEditor, setSysEditor] = useState(null); // {key, nom}
  const [newNom, setNewNom] = useState('');
  const [newFile, setNewFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // Logos par région
  const [logos, setLogos] = useState([]);
  const [logoRegion, setLogoRegion] = useState('');
  const [logoNom, setLogoNom] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [nameDrafts, setNameDrafts] = useState({});
  const [savedRegion, setSavedRegion] = useState(null);
  const [activeTab, setActiveTab] = useState('rapports');

  const loadLogos = useCallback(async () => {
    try {
      const res = await fetch(`${API}/rapports/logos/list`);
      const data = await res.json();
      setLogos(data);
      const drafts = {};
      (data || []).forEach(l => { drafts[l.region] = l.nom_region || ''; });
      setNameDrafts(drafts);
    } catch (e) { /* ignore */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/rapports`);
      setRapports(await res.json());
    } catch (e) { setError('Chargement impossible'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); loadLogos(); }, [load, loadLogos]);

  const handleUploadLogo = async () => {
    if (!logoRegion.trim()) { setError('Indiquez un numéro de région (ex. 89, ou 00 = défaut).'); return; }
    if (!logoFile && !logoNom.trim()) { setError('Ajoutez une image et/ou un nom de région.'); return; }
    setBusy(true); setError(null);
    try {
      const fd = new FormData();
      fd.append('region', logoRegion.trim());
      if (logoNom.trim() !== '') fd.append('nom_region', logoNom.trim());
      if (logoFile) fd.append('image', logoFile);
      const res = await fetch(`${API}/rapports/logos`, { method: 'POST', body: fd });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || 'Erreur'); }
      setLogoRegion(''); setLogoNom(''); setLogoFile(null);
      await loadLogos();
    } catch (e) { setError(e.message || 'Enregistrement logo impossible'); }
    setBusy(false);
  };

  const handleSaveName = async (region) => {
    setBusy(true); setError(null);
    try {
      const fd = new FormData();
      fd.append('region', region);
      fd.append('nom_region', (nameDrafts[region] || '').trim());
      const res = await fetch(`${API}/rapports/logos`, { method: 'POST', body: fd });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || 'Erreur'); }
      setSavedRegion(region);
      setTimeout(() => setSavedRegion(null), 2000);
      await loadLogos();
    } catch (e) { setError(e.message || 'Modification impossible'); }
    setBusy(false);
  };

  const handleDeleteLogo = async (region) => {
    if (!window.confirm(`Supprimer le logo de la région ${region} ?`)) return;
    setBusy(true);
    try {
      await fetch(`${API}/rapports/logos/${encodeURIComponent(region)}`, { method: 'DELETE' });
      await loadLogos();
    } catch (e) { setError('Suppression logo impossible'); }
    setBusy(false);
  };

  const handleAdd = async () => {
    if (!newNom.trim()) { setError('Donnez un nom au rapport.'); return; }
    if (!newFile) { setError('Sélectionnez un fichier PDF.'); return; }
    setBusy(true); setError(null);
    try {
      const fd = new FormData();
      fd.append('nom', newNom.trim());
      fd.append('pdf', newFile);
      const res = await fetch(`${API}/rapports`, { method: 'POST', body: fd });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || 'Erreur'); }
      const created = await res.json();
      setNewNom(''); setNewFile(null);
      await load();
      setEditorId(created.id); // ouvre l'éditeur directement
    } catch (e) { setError(e.message || 'Ajout impossible'); }
    setBusy(false);
  };

  const handleDelete = async (r) => {
    if (r.systeme) return;
    if (!window.confirm(`Supprimer le rapport « ${r.nom} » ?`)) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/rapports/${r.id}`, { method: 'DELETE' });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || 'Erreur'); }
      await load();
    } catch (e) { setError(e.message || 'Suppression impossible'); }
    setBusy(false);
  };

  const handleReplacePdf = async (r, file) => {
    if (!file) return;
    if (file.type !== 'application/pdf') { setError('Le fichier doit être un PDF.'); return; }
    if (!window.confirm(`Remplacer le PDF template du rapport « ${r.nom} » ?\nLes positions des champs déjà placés seront conservées.`)) return;
    setBusy(true); setError(null);
    try {
      const fd = new FormData();
      fd.append('pdf', file);
      const res = await fetch(`${API}/rapports/${r.id}/pdf`, { method: 'POST', body: fd });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || 'Erreur'); }
      await load();
    } catch (e) { setError(e.message || 'Remplacement impossible'); }
    setBusy(false);
  };

  const handlePrint = (r) => {
    const params = [];
    if (currentNoseqdaj) params.push(`noseqdaj=${encodeURIComponent(currentNoseqdaj)}`);
    if (userRegion) params.push(`region=${encodeURIComponent(userRegion)}`);
    const q = params.length ? `?${params.join('&')}` : '';
    openReport({ url: `${API}/rapports/${r.id}/pdf${q}`,
      title: r.nom || r.titre || 'Rapport',
      fileName: buildReportName((r.code || r.nom || 'RAPPORT').toString().toUpperCase().replace(/[^A-Z0-9]/g, ''), currentNoseqdaj) });
  };

  return (
    <div className="popup-overlay" data-testid="modal-gestion-rapports" style={{ zIndex: 10000 }}
         onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
           style={{ background: '#f0f0f0', width: 'min(1040px, 97vw)', height: 'min(760px, 94vh)',
                    display: 'flex', flexDirection: 'column', border: '1px solid #555',
                    boxShadow: '0 8px 30px rgba(0,0,0,0.4)' }}>
        <div style={{ background: '#1e3a8a', color: '#fff', padding: '10px 16px',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 700 }}>Gestion des rapports</span>
          <button data-testid="gestion-rapports-close" onClick={onClose}
                  style={{ background: 'transparent', color: '#fff', border: 'none', fontSize: 20, cursor: 'pointer' }}>✕</button>
        </div>

        {/* Onglets */}
        <div style={{ display: 'flex', gap: 2, padding: '8px 12px 0 12px', background: '#e5e7eb', borderBottom: '1px solid #ccc' }}>
          {[{ id: 'rapports', lbl: 'Rapports' }, { id: 'logos', lbl: 'Logos par région' }].map(t => (
            <button key={t.id} data-testid={`gr-tab-${t.id}`}
                    onClick={() => setActiveTab(t.id)}
                    style={{ padding: '8px 20px', border: '1px solid #ccc', borderBottom: 'none', cursor: 'pointer',
                             fontWeight: 700, fontSize: 13,
                             background: activeTab === t.id ? '#f0f0f0' : '#cbd5e1',
                             color: activeTab === t.id ? '#1e3a8a' : '#475569',
                             borderTopLeftRadius: 6, borderTopRightRadius: 6 }}>
              {t.lbl}{t.id === 'logos' ? ` (${logos.length})` : ''}
            </button>
          ))}
        </div>

        {error && (
          <div data-testid="rapport-error" style={{ padding: '8px 16px', color: '#b91c1c', fontSize: 13 }}>{error}</div>
        )}

        {/* ===== Onglet RAPPORTS ===== */}
        {activeTab === 'rapports' && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div style={{ padding: '12px 16px', background: '#dbe6f0', borderBottom: '1px solid #ccc',
                          display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <label style={{ fontSize: 13, display: 'flex', flexDirection: 'column', gap: 3 }}>
                Nom du rapport
                <input data-testid="rapport-new-nom" value={newNom} onChange={e => setNewNom(e.target.value)}
                       style={{ padding: '5px 8px', minWidth: 220 }} />
              </label>
              <label style={{ fontSize: 13, display: 'flex', flexDirection: 'column', gap: 3 }}>
                Fichier PDF (template)
                <input data-testid="rapport-new-file" type="file" accept="application/pdf"
                       onChange={e => setNewFile(e.target.files?.[0] || null)} style={{ fontSize: 12 }} />
              </label>
              <button className="win-btn" data-testid="rapport-add-btn" disabled={busy} onClick={handleAdd}
                      style={{ padding: '6px 16px', background: '#16a34a', color: '#fff', fontWeight: 700 }}>
                + Ajouter
              </button>
            </div>
            <div style={{ flex: 1, overflow: 'auto', padding: 8 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, background: '#fff' }}>
                <thead>
                  <tr style={{ background: '#1e40af', color: '#fff', textAlign: 'left' }}>
                    <th style={{ padding: '7px 10px' }}>Nom</th>
                    <th style={{ padding: '7px 10px' }}>Source</th>
                    <th style={{ padding: '7px 10px' }}>Menu Formulaires</th>
                    <th style={{ padding: '7px 10px' }}>Menu Impression</th>
                    <th style={{ padding: '7px 10px', width: 340 }}>Actions</th>
                  </tr>
                </thead>
                <tbody data-testid="rapports-list">
                  {loading && <tr><td colSpan={5} style={{ padding: 14 }}>Chargement…</td></tr>}
                  {!loading && rapports.length === 0 && (
                    <tr><td colSpan={5} style={{ padding: 14, color: '#666' }}>Aucun rapport. Ajoutez-en un ci-dessus.</td></tr>
                  )}
                  {rapports.map(r => (
                    <tr key={r.id} data-testid={`rapport-row-${r.id}`} style={{ borderBottom: '1px solid #eee', height: 34 }}>
                      <td style={{ padding: '6px 10px' }}>
                        {r.nom}
                      </td>
                      <td style={{ padding: '6px 10px', color: '#555' }}>
                        {r.table_source ? `${r.base_source || '?'}.${r.table_source}` : '—'}
                      </td>
                      <td style={{ padding: '6px 10px' }} data-testid={`rapport-menu-formulaire-${r.id}`}>{r.dans_menu ? '✓' : ''}</td>
                      <td style={{ padding: '6px 10px' }} data-testid={`rapport-menu-impression-${r.id}`}>{r.dans_menu_impression ? '✓' : ''}</td>
                      <td style={{ padding: '6px 10px', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {!r.systeme && (
                          <button className="win-btn" data-testid={`rapport-edit-${r.id}`} onClick={() => setEditorId(r.id)}
                                  style={{ padding: '3px 10px' }}>Éditer</button>
                        )}
                        {!r.systeme && (
                          <label className="win-btn" data-testid={`rapport-replace-${r.id}`}
                                 title="Remplacer le PDF template de ce rapport"
                                 style={{ padding: '3px 10px', cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1 }}>
                            Remplacer PDF
                            <input type="file" accept="application/pdf" disabled={busy}
                                   data-testid={`rapport-replace-input-${r.id}`}
                                   style={{ display: 'none' }}
                                   onChange={e => { const f = e.target.files?.[0] || null; e.target.value = ''; handleReplacePdf(r, f); }} />
                          </label>
                        )}
                        {r.systeme && SYSTEM_KEYS[r.nom] && (
                          <button className="win-btn" data-testid={`rapport-edit-sys-${r.id}`}
                                  onClick={() => setSysEditor({ key: SYSTEM_KEYS[r.nom], nom: r.nom })}
                                  style={{ padding: '3px 10px' }}>Éditer</button>
                        )}
                        {!r.systeme && (
                          <button className="win-btn" data-testid={`rapport-print-${r.id}`} onClick={() => handlePrint(r)}
                                  style={{ padding: '3px 10px' }}>Imprimer</button>
                        )}
                        {!r.systeme && (
                          <button className="win-btn" data-testid={`rapport-delete-${r.id}`} disabled={busy}
                                  onClick={() => handleDelete(r)}
                                  style={{ padding: '3px 10px', background: '#dc2626', color: '#fff' }}>Suppr.</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ===== Onglet LOGOS ===== */}
        {activeTab === 'logos' && (
          <div data-testid="rapport-logos-panel" style={{ flex: 1, overflow: 'auto', padding: 16 }}>
            <div style={{ fontSize: 12, color: '#555', marginBottom: 8 }}>
              Un logo par numéro de région (utilisez <b>00</b> pour le logo par défaut).
              Le champ « Logo » posé sur un rapport affiche automatiquement le logo de la région de la demande.
              Le nom de région (ex. 90 → Côte-Nord) alimente le champ « Nom de la région ».
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 14,
                          background: '#dbe6f0', padding: 10, border: '1px solid #cbd5e1' }}>
              <label style={{ fontSize: 12, display: 'flex', flexDirection: 'column', gap: 3 }}>
                N° région
                <input data-testid="logo-region-input" value={logoRegion} onChange={e => setLogoRegion(e.target.value)}
                       placeholder="ex. 89" style={{ padding: '5px 8px', width: 90 }} />
              </label>
              <label style={{ fontSize: 12, display: 'flex', flexDirection: 'column', gap: 3 }}>
                Nom de la région
                <input data-testid="logo-nom-input" value={logoNom} onChange={e => setLogoNom(e.target.value)}
                       placeholder="ex. Côte-Nord" style={{ padding: '5px 8px', width: 180 }} />
              </label>
              <label style={{ fontSize: 12, display: 'flex', flexDirection: 'column', gap: 3 }}>
                Image (PNG/JPG) — optionnel
                <input data-testid="logo-file-input" type="file" accept="image/png,image/jpeg"
                       onChange={e => setLogoFile(e.target.files?.[0] || null)} style={{ fontSize: 12 }} />
              </label>
              <button className="win-btn" data-testid="logo-upload-btn" disabled={busy} onClick={handleUploadLogo}
                      style={{ padding: '6px 14px', background: '#2563eb', color: '#fff', fontWeight: 700 }}>
                Enregistrer
              </button>
            </div>
            <div data-testid="logos-list" style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {logos.length === 0 && <span style={{ fontSize: 12, color: '#888' }}>Aucune région configurée. Ajoutez-en une.</span>}
              {logos.map(l => (
                <div key={l.region} data-testid={`logo-item-${l.region}`}
                     style={{ border: '1px solid #ddd', padding: 6, textAlign: 'center', width: 150, background: '#fff' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 2 }}>
                    Région {l.region}{l.region === '00' && ' (défaut)'}
                  </div>
                  {l.has_image ? (
                    <img src={`${API}/rapports/logos/${encodeURIComponent(l.region)}/image?t=${Date.now()}`}
                         alt={`logo ${l.region}`}
                         style={{ maxWidth: '100%', maxHeight: 60, objectFit: 'contain', background: '#f8fafc' }} />
                  ) : (
                    <div style={{ fontSize: 10, color: '#999', padding: '14px 0' }}>Aucune image</div>
                  )}
                  <input data-testid={`logo-nom-edit-${l.region}`}
                         value={nameDrafts[l.region] ?? ''}
                         onChange={e => setNameDrafts(d => ({ ...d, [l.region]: e.target.value }))}
                         placeholder="Nom de la région"
                         style={{ width: '100%', marginTop: 6, padding: '3px 5px', fontSize: 11, boxSizing: 'border-box' }} />
                  <button className="win-btn" data-testid={`logo-save-nom-${l.region}`} disabled={busy}
                          onClick={() => handleSaveName(l.region)}
                          style={{ marginTop: 4, padding: '2px 8px', fontSize: 11, width: '100%',
                                   background: savedRegion === l.region ? '#16a34a' : '#2563eb', color: '#fff' }}>
                    {savedRegion === l.region ? '✓ Enregistré' : 'Enregistrer le nom'}
                  </button>
                  <button className="win-btn" data-testid={`logo-delete-${l.region}`} disabled={busy}
                          onClick={() => handleDeleteLogo(l.region)}
                          style={{ marginTop: 4, padding: '2px 8px', fontSize: 11, width: '100%', background: '#dc2626', color: '#fff' }}>
                    Supprimer
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {editorId && (
        <RapportEditor rapportId={editorId}
                       onClose={() => setEditorId(null)}
                       onSaved={load} />
      )}
      {sysEditor && (
        <SystemReportEditor reportKey={sysEditor.key} nom={sysEditor.nom}
                            onClose={() => setSysEditor(null)} />
      )}
    </div>
  );
}
